'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { noop } from 'utils/noop';

type ControlledComponentOptions = {
  skipDefaultUpdates: boolean;
};

/**
 * A small abstraction which allows a component to easily
 * be both controlled or uncontrolled.
 *
 * @example:
 *
 * const [value, setValue] = useControlledComponent(valueProp, onRequestChange)
 *
 * <input value={value} onChange={(e) => setValue(e.target.value)} />
 *
 * @param {*} controlledProp a controlled prop
 * @param {Function} onRequestChange a controlled prop change function
 * @param {*} defaultState the default state to use if not controlled
 * @param {ControlledComponentOptions} options if true, when the defaultValue changes, the
 * hook won't update the value to the new defaultValue.
 */

export function useControlledComponent<T>(
  controlledProp: T | undefined,
  onRequestChange: (next: T) => void = noop,
  defaultState: T | undefined,
  options: ControlledComponentOptions = { skipDefaultUpdates: false }
): [T | undefined, (next: T) => void] {
  const { skipDefaultUpdates } = options;
  const [state, setState] = useState<T | undefined>(defaultState);

  // If the default value is initially passed in as null or undefined
  // but is subsequently updated with a value, let's update our state.
  // This mirrors the uncontrolled behavior of inputs.
  const currentStateRef = useRef(state);

  useEffect(() => {
    currentStateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (skipDefaultUpdates) return;
    // when our defaultState changes, see if the current state is
    // null, and if so, update it.
    if (currentStateRef.current == null && defaultState != null) {
      setState(defaultState);
    }
  }, [defaultState, skipDefaultUpdates]);

  const isControlled = typeof controlledProp !== 'undefined';

  const onChangeRef = useRef(onRequestChange);
  onChangeRef.current = onRequestChange;

  const handleChange = useCallback(
    (nextState: T) => {
      if (!isControlled) {
        setState(nextState);
      }

      onChangeRef.current(nextState);
    },
    [isControlled]
  );

  return [isControlled ? controlledProp : state, handleChange];
}
